<template>
	<div>
		<div class="row content-header">
			<div class="content-header-left mb-2 col-md-9 col-12">
				<div class="row breadcrumbs-top">
					<div class="col-12 d-flex align-items-center">
						<feather-icon icon="ArrowLeftIcon" size="24" class="mr-2" @click="goBack" />
						<h2 class="content-header-title mb-0">Raw Material • Add Purchase</h2>
					</div>
				</div>
			</div>
		</div>

		<validation-observer ref="simpleRules">
			<b-form method="post" @submit="submitPurchase">
				<b-card>
					<b-row>
						<!-- Purchase Date -->
						<b-col md="6">
							<b-form-group>
								<template v-slot:label>Purchase Date <span class="text-danger">*</span></template>
								<validation-provider #default="{ errors }" name="Purchase Date" rules="required">
									<b-form-datepicker
										name="purchased_at"
										v-model="form.purchased_at"
										menu-class="w-100"
										calendar-width="100%"
										class="mb-1"
										:max="maxDate"
										locale="en"
									/>
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<!-- Raw Material -->
						<b-col md="6">
							<b-form-group>
								<template v-slot:label>Raw Material <span class="text-danger">*</span></template>
								<validation-provider #default="{ errors }" name="Raw Material" rules="required">
									<v-select
										v-model="selectedRawMaterial"
										:options="rawMaterialOptions"
										label="name"
										:reduce="(rm) => rm"
										:clearable="false"
										@input="onRawMaterialChange"
									/>
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<!-- Quantity + Unit -->
						<b-col md="5">
							<b-form-group>
								<template v-slot:label>Quantity <span class="text-danger">*</span></template>
								<validation-provider
									#default="{ errors }"
									name="Quantity"
									:rules="{
										required: true,
										regex: RegExp(/^\d+(\.\d{1,4})?$/),
									}"
								>
									<b-form-input
										v-model="form.quantity"
										placeholder="Quantity"
										type="text"
										name="quantity"
										maxlength="12"
									/>
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<b-col md="1" class="d-flex align-items-center">
							<div class="form-label-group font-weight-bold">
								{{ unitLabel }}
							</div>
						</b-col>

						<!-- Unit Cost -->
						<b-col md="6">
							<b-form-group>
								<template v-slot:label>Unit Cost (Rs.) <span class="text-danger">*</span></template>
								<validation-provider
									#default="{ errors }"
									name="Unit Cost"
									:rules="{
										required: true,
										regex: RegExp(/^\d+(\.\d{0,2})?$/),
									}"
								>
									<b-form-input
										v-model="form.unit_cost"
										placeholder="Unit Cost (Rs.)"
										type="text"
										name="unit_cost"
										maxlength="12"
									/>
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<!-- Batch No -->
						<!-- <b-col md="6">
							<b-form-group>
								<template v-slot:label>Batch No. <span class="text-danger">*</span></template>
								<validation-provider #default="{ errors }" name="Batch No." rules="required">
									<b-form-input
										v-model="form.batch_no"
										placeholder="Batch No."
										type="text"
										name="batch_no"
										maxlength="40"
									/>
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col> -->

						<!-- Submit -->
						<b-col md="12">
							<b-button type="submit" variant="primary" class="mr-1 mb-3">Submit</b-button>
							<b-button type="button" variant="outline-secondary" class="mb-3" @click="goBack">Cancel</b-button>
						</b-col>
					</b-row>
				</b-card>
			</b-form>
		</validation-observer>
	</div>
</template>

<script>
import vSelect from "vue-select";
import axiosIns from "@/libs/axios";
import moment from "moment";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, regex } from "@validations";

export default {
	components: {
		vSelect,
		ValidationProvider,
		ValidationObserver,
	},
	data() {
		return {
			required,
			regex,
			userData: null,

			rawMaterialOptions: [], // from GET web/company/{id}/raw-materials
			selectedRawMaterial: null, // whole object (so we can read unit)

			unitLabel: "", // shows Kg, etc.

			form: {
				purchased_at: null, // YYYY-MM-DD from b-form-datepicker
				raw_material_id: null,
				quantity: "",
				unit_cost: "",
				batch_no: "",
			},

			maxDate: new Date().toISOString().split("T")[0],
		};
	},
	created() {
		this.userData = JSON.parse(sessionStorage.getItem("userData") || localStorage.getItem("userData") || "null");
		this.fetchRawMaterials();
	},
	methods: {
		goBack() {
			this.$router.go(-1);
		},

		// Load dropdown options
		fetchRawMaterials(page = 1) {
			const companyId = this.userData && this.userData.company ? this.userData.company.id : null;
			const url = `web/company/${companyId}/raw-materials`;

			axiosIns
				.get(url)
				.then((res) => {
					// API screenshot shows: { raw_materials: { data: [...] } }
					const list =
						res && res.data && res.data.raw_materials && res.data.raw_materials.data ? res.data.raw_materials.data : [];

					this.rawMaterialOptions = list;
				})
				.catch((error) => {
					// eslint-disable-next-line no-console
					console.error(error && error.response ? error.response?.data : error);
				});
		},

		onRawMaterialChange(rm) {
			// rm is the selected raw material object
			if (rm && rm.unit && rm.unit.name) {
				this.unitLabel = rm.unit.name;
			} else {
				this.unitLabel = "";
			}
			this.form.raw_material_id = rm ? rm.id : null;
		},

		submitPurchase(e) {
			e.preventDefault();
			this.$refs.simpleRules.validate().then((ok) => {
				if (!ok) return;

				// Build form-data for backend
				const companyId = this.userData && this.userData.company ? this.userData.company.id : null;
				const url = `web/company/${companyId}/rm-purchases`;

				const fd = new FormData();
				// ensure proper date formatting
				const dateStr = this.form.purchased_at ? moment(this.form.purchased_at).format("YYYY-MM-DD") : "";

				fd.append("purchased_at", dateStr);
				fd.append("raw_material_id", this.form.raw_material_id);
				fd.append("quantity", this.form.quantity);
				fd.append("unit_cost", this.form.unit_cost);

				axiosIns
					.post(url, fd)
					.then(() => {
						this.$toast({
							component: ToastificationContent,
							position: "top-right",
							props: {
								icon: "CoffeeIcon",
								variant: "success",
								text: "Raw material purchase added successfully",
							},
						});
						this.goBack(); // or push to your list route if preferred
					})
					.catch((error) => {
						const errs =
							error && error.response && error.response?.data && error.response?.data?.errors
								? error.response?.data?.errors
								: null;
						const msg = errs ? Object.values(errs).flat().join(", ") : "Failed to add purchase";
						this.$toast({
							component: ToastificationContent,
							position: "top-right",
							props: { icon: "CoffeeIcon", variant: "danger", text: msg },
						});
					});
			});
		},
	},
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";

.expiry-date {
	transform: translate3d(0px, -377px, 0px);
	top: 0px;
}
</style>